import { BackendModule, InitOptions, ReadCallback, Services } from 'i18next';
import { fetchNamespacedTemplates, TemplateResponse } from 'src/api/template';

class BackendError extends Error {
    retry: boolean;

    constructor(message, retry = false) {
        super(message);

        this.retry = retry;
    }
}

interface BackendOptions {}

interface Resource {
    [k: string]: Resource | string;
}

function normalize<T>(x: T | T[]): T[] {
    return Array.isArray(x) ? x : [x];
}

class I18NextBackend implements BackendModule<BackendOptions> {
    static type = 'backend';
    type: 'backend' = 'backend';

    services!: Services;
    options: BackendOptions = {};

    constructor(services: Services, options: BackendOptions, i18nextOptions: InitOptions) {
        this.init(services, options, i18nextOptions);
    }

    init(services: Services, options: BackendOptions, i18nextOptions: InitOptions) {
        this.services = services;
        this.options = {
            ...this.options,
            ...options,
        };
    }

    read(locale: string, namespace: string, callback: ReadCallback) {
        const [language, region] = this.splitIntoLangRegion(locale);

        this.fetchTranslationTemplates(language, region, namespace)
            .then(res => {
                callback(null, res);
            })
            .catch(e => {
                if (e instanceof BackendError) {
                    callback(e, e.retry);
                }
            });

        // this.loadTranslationFiles(language, namespace)
        //     .then(filesResource => {
        //         return this.fetchTranslationTemplates(language, region, namespace).then(templatesResource => {
        //             callback(null, merge(filesResource, templatesResource));
        //         });
        //     })
        //     .catch(e => {
        //         if (e instanceof BackendError) {
        //             callback(e, e.retry);
        //         }
        //     });
    }

    /**
     * Load our translation files
     */
    loadTranslationFiles(language: string, namespace: string): Promise<Resource> {
        const url = `${process.env.PUBLIC_URL}/locales/${language}/${namespace}.json`;

        return fetch(url)
            .then(
                response => {
                    const { ok, status } = response;

                    if (!ok) {
                        // don't retry for 4xx codes
                        const retry = status >= 500 && status < 600;

                        throw new BackendError(`failed loading translation file ${url}`, retry);
                    }

                    return response.text();
                },
                () => {
                    throw new BackendError(`failed loading translation file ${url}`);
                }
            )
            .then(data => {
                try {
                    return Promise.resolve(JSON.parse(data));
                } catch (e) {
                    throw new BackendError(`failed parsing translation file ${url}`);
                }
            });
    }

    /**
     * Fetch the translation templates from lamante backend
     */
    fetchTranslationTemplates(language: string, region: string, namespace: string): Promise<Resource> {
        return fetchNamespacedTemplates(language, region, intoExtNamespaceKey(namespace))
            .then(
                ({ response, error }) => {
                    if (error) {
                        // Backend returns 404 if translation key does not exist
                        if ((error.code = 404)) return [];
                        // don't retry for 4xx codes
                        const retry = error.code >= 500 && error.code < 600;

                        throw new BackendError(error.message, retry);
                    }
                    return normalize(response!.data).filter(x => x !== undefined);
                },
                () => {
                    throw new BackendError(`failed loading templates for namespace ${namespace}`);
                }
            )
            .then(templates => {
                try {
                    return Promise.resolve(this.parseTemplates(templates, namespace));
                } catch (e) {
                    throw new BackendError(`failed parsing ${namespace} templates to json`, false);
                }
            });
    }

    /**
     * "Parse" / Transform Response from backend to resource for i18next
     */
    parseTemplates(data: TemplateResponse[], namespace: string): Resource {
        const resource = {};

        data.forEach(template => {
            // Skip everything which is not plain text
            if (template.type !== 'PLAIN_TEXT') return;
            // Skip everything which has no templateKey
            // Requesting a NAMESPACE key will always yield results with templateKey
            if (template.templateKey === undefined) return;

            const keys = this.normalizeTplKey(template.templateKey, namespace).split('.');

            this.walk(resource, keys, template.value);
        });

        return resource;
    }

    /**
     * Recursivly crete the resource object
     */
    walk(resource: Resource, keys: string[], value: string): Resource {
        const key = keys.shift()!;

        if (keys.length > 0) {
            if (resource[key] === undefined) resource[key] = {};

            this.walk(resource[key] as Resource, keys, value);
        } else {
            resource[key] = value;
        }

        return resource;
    }

    /**
     * The Backend imposes some rules for the template keys
     * - new / most template keys start with TPL_
     * - namespace template keys are prefixed with TPL_NS_
     * - while not a strict requirement the keys are uppercase
     *
     * - the countries namespace key will deliver template keys which are prefixed with COUNTRY_
     * - the languages namespace key will deliver template keys which are prefixed with LANGUAGE_
     *
     */
    normalizeTplKey(key: string, ns: string): string {
        let norm = key.replace('TPL_', '');

        switch (ns) {
            case 'countries':
                norm = norm.replace('COUNTRY_', '');
                break;
            case 'languages':
                norm = norm.replace('LANGUAGE_', '');
                break;
        }

        return norm.toLocaleLowerCase();
    }

    splitIntoLangRegion(locale: string): string[] {
        return locale.split('-');
    }

    /**
     * Save the missing translation
     * This needs to talk to the lamante backend at some point
     */
    create(languages: string[], namespace: string, key: string, fallbackValue: string) {
        console.log('i18next - lamante backend - create -> !not implemented!');
    }
    //   /** Load multiple languages and namespaces. For backends supporting multiple resources loading */
    //   readMulti?(languages: string[], namespaces: string[], callback: ReadCallback): void;
    //   /** Store the translation. For backends acting as cache layer */
    //   save?(language: string, namespace: string, data: ResourceLanguage): void;
}

export default I18NextBackend;

export function intoExtNamespaceKey(ns: string): string {
    return `TPL_NS_${ns.toUpperCase()}`;
}

export function intoExtKey(ns: string, key: string): string {
    let prefix: string;

    switch (ns) {
        case 'countries':
            prefix = 'COUNTRY';
            break;
        case 'languages':
            prefix = 'LANGUAGE';
            break;
        default:
            prefix = 'TPL';
    }
    return `${prefix}_${key.toUpperCase()}`;
}
